import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewEncapsulation,
  inject,
  DOCUMENT,
  DestroyRef,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ConnectivityService } from '../../core/services/connectivity/connectivity.service';
import { SessionService } from '../../core/services/session/session.service';
import { SharedApisService } from '../../core/services/shared-apis/shared-apis.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { BREAK_POINTS } from '../../shared/constants/break-points.constant';
import { ScreenType } from '../../shared/enums/screen-type.enum';
import { DelegatedUIErrorI } from '../../shared/interfaces/delegated-ui-error.interface';
import { FooterComponent } from '../layout/footer/footer.component';
import { HeaderComponent } from '../layout/header/header.component';
import { SideMenuComponent } from '../layout/side-menu/side-menu.component';
import { Role } from '../../shared/enums/role.enum';
import { StorageService } from '../../core/services/storage/storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-base',
  imports: [RouterOutlet, HeaderComponent, FooterComponent, SideMenuComponent],
  templateUrl: './base.component.html',
  styleUrl: './base.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class BaseComponent implements OnInit {
  private readonly sessionService = inject(SessionService);
  private readonly renderer = inject(Renderer2);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly sharedApisService = inject(SharedApisService);
  private readonly toastService = inject(ToastService);
  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private readonly platformID: Object
  ) {
    inject(NgZone).runOutsideAngular(() => {
      this.connectivityService.startChecking();
    });
  }
  ngOnInit(): void {
    this.findLoggedInUser();
    this.initScreenType();
    this.initIsSideMenuListener();
  }

  @HostListener('window:resize')
  onResize() {
    this.addScreenTypeAndIsSideMenuOpen(window.innerWidth);
  }

  findLoggedInUser() {
    this.sharedApisService
      .findLoggedInUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (findLoggedInUserResponse) => {
          const { data } = findLoggedInUserResponse;
          const isAdmin = data.roles.includes(Role.ADMIN);
          const isSuperAdmin = data.roles.includes(Role.SUPER_ADMIN);
          const isDean = data.roles.includes(Role.DEAN);
          const isProgramManager = data.roles.includes(Role.PROGRAM_MANAGER);
          const isDepartmentManager = data.roles.includes(
            Role.DEPARTMENT_MANAGER
          );

          if (
            !isAdmin &&
            !isSuperAdmin &&
            !isDean &&
            !isProgramManager &&
            !isDepartmentManager
          ) {
            this.toastService.showError(
              'Only  Deans , Department Managers And Program Managers Can Use This System'
            );
            this.storageService.removeItem('accessToken');
          } else
            this.sessionService.addLoggedInUser(findLoggedInUserResponse.data);
        },
        error: (err: DelegatedUIErrorI) => {
          this.toastService.showError(err.description, err.title);
        },
      });
  }

  initIsSideMenuListener() {
    this.sessionService
      .getIsSideMenuOpen$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isSideMenuOpen) => {
          const appBase = this.renderer.selectRootElement('app-base', true);
          if (isSideMenuOpen) {
            this.renderer.setStyle(
              appBase,
              'grid-template-columns',
              '169px minmax(0, 1fr)'
            );
          } else {
            this.renderer.setStyle(
              appBase,
              'grid-template-columns',
              '60px minmax(0, 1fr)'
            );
          }
        },
      });
  }

  initScreenType() {
    if (isPlatformBrowser(this.platformID)) {
      this.addScreenTypeAndIsSideMenuOpen(
        this.document.defaultView!.innerWidth
      );
    }
  }

  addScreenTypeAndIsSideMenuOpen(width: number) {
    if (width < BREAK_POINTS.mobile) {
      this.sessionService.addIsSideMenuOpen(false);
      this.sessionService.addScreenType(ScreenType.MOBILE);
    } else if (width < BREAK_POINTS.tablet + 9) {
      this.sessionService.addIsSideMenuOpen(false);
      this.sessionService.addScreenType(ScreenType.TABLET);
    } else {
      this.sessionService.addIsSideMenuOpen(true);
      this.sessionService.addScreenType(ScreenType.PC);
    }
  }
}
